import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { PillWithIcon } from '@els/els-react--pill';
import Breadcrumb from '@Services/Breadcrumb';
import { getLatestSummary } from './selectors';
import { isSummaryContextStale } from './hooks/useEntryStaleness';
import { resolveSummaryChain } from './summaryContext';

import './InSummaryConditionIndicatorContainer.scss';

function summaryReferencesCondition(summary, activeBreadcrumb, reactionId, conditionId) {
  if (!summary || isSummaryContextStale(summary, activeBreadcrumb)) {
    return false;
  }

  return summary.summaryResponse?.referencedReactions?.some((reaction) => {
    const reactionMatches = String(reaction?.id) === String(reactionId);
    const conditionMatches = reaction?.referencedConditionIds?.some(
      (referencedConditionId) => String(referencedConditionId) === String(conditionId)
    );
    return reactionMatches && conditionMatches;
  });
}

export function InSummaryConditionIndicatorContainer({
  reactionId,
  conditionId,
  databaseId,
  resultName,
  activeBreadcrumb,
  rootBreadcrumb,
  latestSummary,
  fromRT,
}) {
  const isReferenced = useMemo(() => {
    const isAssistantEligible = activeBreadcrumb?.assistantEligible ?? rootBreadcrumb?.assistantEligible;
    if (fromRT || !databaseId || !resultName || isAssistantEligible !== true) {
      return false;
    }

    return summaryReferencesCondition(latestSummary, activeBreadcrumb, reactionId, conditionId);
  }, [activeBreadcrumb, conditionId, databaseId, fromRT, latestSummary, reactionId, resultName, rootBreadcrumb?.assistantEligible]);

  return isReferenced ? (
    <PillWithIcon
      className="ai-search-condition-indicator"
      condensed
      iconColor="text"
      iconProps={{
        a11y: { name: 'Referenced in summary' },
        isImageRole: true,
        size: 'xs',
        sprite: 'AI',
      }}
      pillColor="extended-blue-1"
      textColor="text"
    />
  ) : null;
}

const mapStateToProps = (state, { databaseId, resultName }) => {
  const activeBreadcrumb = Breadcrumb.getBreadcrumbForDbByResultName(databaseId, resultName);
  const { rootBreadcrumb, chainKey } = resolveSummaryChain(activeBreadcrumb, databaseId);

  return {
    activeBreadcrumb,
    rootBreadcrumb,
    latestSummary: getLatestSummary(state, chainKey),
  };
};

InSummaryConditionIndicatorContainer.propTypes = {
  reactionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  conditionId: PropTypes.string.isRequired,
  databaseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  resultName: PropTypes.string.isRequired,
  activeBreadcrumb: PropTypes.object,
  rootBreadcrumb: PropTypes.object,
  latestSummary: PropTypes.object,
  fromRT: PropTypes.bool,
};

export default connect(mapStateToProps)(InSummaryConditionIndicatorContainer);
